import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { FaPlus, FaTrash, FaEdit, FaSave } from "react-icons/fa";
import Loader from "../../components/Loader";
import NotFound from "../../components/ul/NotFound";
import generateCaptcha from "../../components/captcha";
import Calling from "../../api/Calling";

const WithdrawCard = () => {
  const [loader, setLoader] = useState(false);
  const [cards, setCards] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editForm, setEditForm] = useState({ w_amount: "", w_type: "" });
  const [newCard, setNewCard] = useState({ w_amount: 1, w_type: "upi" });
  const [selectedType, setSelectedType] = useState("all");
  const [selectedCards, setSelectedCards] = useState([]);

  const withdrawTypes = ["all", "upi", "paypal", "binance"];

  const fetchData = async () => {
    setLoader(true);
    try {
      const resp = await Calling.getWithdrawCard();
      resp?.status ? setCards(resp?.data) : toast.error(resp?.message);
    } catch (error) {
      console.error(error);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filteredCards =
    selectedType === "all"
      ? cards
      : cards.filter((item) => item.w_type === selectedType);

  const handleEdit = (id) => {
    const card = cards.find((i) => i._id === id);
    setEditForm({ w_amount: card.w_amount, w_type: card.w_type });
    setEditingId(id);
  };

  const handleSave = async (id) => {
    if (!editForm.w_amount || editForm.w_amount < 1)
      return toast.error("Minimum amount is 1");

    setLoader(true);
    try {
      const resp = await Calling.updateWithdrawCard(id, editForm);
      resp?.status && toast.success(resp?.message);
      fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setEditingId(null);
      setLoader(false);
    }
  };

  const handleAddSubmit = async () => {
    if (!newCard.w_amount || newCard.w_amount < 1)
      return toast.error("Minimum amount is 1");

    setLoader(true);
    try {
      const resp = await Calling.addWithdrawCard(newCard);
      if (resp?.status) {
        toast.success(resp?.message);
        setNewCard({ w_amount: 1, w_type: "upi" });
        setShowAddForm(false);
        fetchData();
      } else toast.error(resp?.message);
    } catch (err) {
      console.error(err);
    } finally {
      setLoader(false);
    }
  };

  const handleBulkDelete = async (ids) => {
    const content = `Delete ${ids.length} selected items?`;
    const check = generateCaptcha(content);
    if (!check) return toast.error("Answer is not correct");

    setLoader(true);
    try {
      const resp = await Calling.deleteWithdrawCard({ id: ids });
      resp?.status && toast.success(resp?.message);
      setSelectedCards([]);
      fetchData();
    } catch (err) {
      console.error(err);
    } finally {
      setLoader(false);
    }
  };

  const toggleSelect = (id) => {
    selectedCards.includes(id)
      ? setSelectedCards(selectedCards.filter((x) => x !== id))
      : setSelectedCards([...selectedCards, id]);
  };

  return (
    <>
      {loader ? (
        <Loader loader={loader} />
      ) : (
        <div className="md:ml-[4.4rem] px-4 mt-16 w-full text-main-font bg-main-bg min-h-screen pb-10">
          {/* Header */}
          <div className="flex flex-wrap gap-3 justify-between items-center pt-6 px-6">
            <h1 className="text-3xl font-extrabold text-main-font">
              Withdraw Card Management
            </h1>

            <div className="flex flex-wrap gap-3">
              {selectedCards.length > 0 && (
                <button
                  onClick={() => handleBulkDelete(selectedCards)}
                  className="px-5 py-2 bg-main-delete text-main-white rounded-xl flex items-center gap-2 font-semibold hover:opacity-90 transition-all"
                >
                  <FaTrash /> Delete Selected
                </button>
              )}

              <button
                onClick={() => setShowAddForm(!showAddForm)}
                className="px-5 py-2 bg-main-blue text-white rounded-xl flex items-center gap-2 font-semibold hover:opacity-90 transition-all"
              >
                <FaPlus /> Add Card
              </button>
            </div>
          </div>

          {/* Filter Buttons Desktop View */}
          <div className="hidden sm:flex gap-3 px-6 mt-4 flex-wrap">
            {withdrawTypes.map((t) => (
              <button
                key={t}
                onClick={() => setSelectedType(t)}
                className={`px-5 py-2 rounded-xl capitalize font-semibold shadow-sm transition-all 
                ${
                  selectedType === t
                    ? "bg-main-blue text-white"
                    : "bg-main-th text-main-font hover:bg-main-thHover"
                }`}
              >
                {t}
              </button>
            ))}
          </div>

          {/* Filter Dropdown Mobile View */}
          <div className="flex sm:hidden px-6 mt-4">
            <select
              value={selectedType}
              onChange={(e) => setSelectedType(e.target.value)}
              className="w-full p-3 border border-main-border rounded-xl font-semibold text-main-font capitalize bg-main-white"
            >
              {withdrawTypes.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
          </div>

          {/* Add Form */}
          {showAddForm && (
            <div className="bg-main-white border border-main-border p-5 m-6 rounded-2xl shadow-md">
              <h2 className="text-xl font-bold mb-4 text-main-font">
                Add Withdraw Card
              </h2>
              <div className="grid sm:grid-cols-12 gap-4">
                <input
                  type="number"
                  min={1}
                  placeholder="Amount"
                  value={newCard.w_amount}
                  onChange={(e) =>
                    setNewCard({ ...newCard, w_amount: Number(e.target.value) })
                  }
                  className="sm:col-span-4 p-3 border border-main-border rounded-xl font-semibold text-main-font"
                />

                <select
                  value={newCard.w_type}
                  onChange={(e) =>
                    setNewCard({ ...newCard, w_type: e.target.value })
                  }
                  className="sm:col-span-4 p-3 border border-main-border rounded-xl capitalize"
                >
                  {withdrawTypes
                    .filter((v) => v !== "all")
                    .map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                </select>

                <button
                  onClick={handleAddSubmit}
                  className="sm:col-span-2 bg-main-blue text-white rounded-xl font-bold hover:opacity-90 transition-all"
                >
                  Submit
                </button>
              </div>
            </div>
          )}

          {/* Card List */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {filteredCards.length > 0 ? (
              filteredCards.map((item) => (
                <div
                  key={item._id}
                  className="p-6 border border-main-border rounded-2xl shadow bg-main-white hover:shadow-lg transition-all relative flex flex-col gap-3"
                >
                  <input
                    type="checkbox"
                    className="absolute top-3 right-3 scale-125"
                    checked={selectedCards.includes(item._id)}
                    onChange={() => toggleSelect(item._id)}
                  />

                  {editingId === item._id ? (
                    <>
                      <input
                        type="number"
                        min={1}
                        value={editForm.w_amount}
                        onChange={(e) =>
                          setEditForm({
                            ...editForm,
                            w_amount: Number(e.target.value),
                          })
                        }
                        className="w-full p-2 border border-main-border rounded-xl"
                      />

                      <select
                        value={editForm.w_type}
                        onChange={(e) =>
                          setEditForm({ ...editForm, w_type: e.target.value })
                        }
                        className="w-full p-2 border border-main-border rounded-xl capitalize"
                      >
                        {withdrawTypes
                          .filter((v) => v !== "all")
                          .map((t) => (
                            <option key={t} value={t}>
                              {t}
                            </option>
                          ))}
                      </select>
                    </>
                  ) : (
                    <>
                      <h2 className="text-3xl font-extrabold text-main-blue">
                        ₹ {item.w_amount}
                      </h2>
                      <p className="capitalize text-main-subfont font-semibold text-lg">
                        {item.w_type}
                      </p>
                    </>
                  )}

                  <div className="flex gap-3 pt-3">
                    <button
                      onClick={() =>
                        editingId === item._id
                          ? handleSave(item._id)
                          : handleEdit(item._id)
                      }
                      className="px-5 py-2 bg-main-blue text-white rounded-xl flex items-center gap-2 font-bold hover:opacity-90 transition-all"
                    >
                      {editingId === item._id ? (
                        <>
                          <FaSave /> Save
                        </>
                      ) : (
                        <>
                          <FaEdit /> Edit
                        </>
                      )}
                    </button>

                    <button
                      onClick={() => handleBulkDelete([item._id])}
                      className="px-5 py-2 bg-main-delete text-white rounded-xl flex items-center gap-2 font-bold hover:opacity-90 transition-all"
                    >
                      <FaTrash /> Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <NotFound />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default WithdrawCard;
